import {
  ANALYTICS_CONVERSION_RATE_URL,
  ANALYTICS_DECLINED_ORDERS_URL,
  ANALYTICS_ORDERS_URL,
  ANALYTICS_STORE_SALES_URL,
  ANALYTICS_PRODUCT_VIEWS_URL,
  ANALYTICS_WISHLIST_URL,
  MARKETING_ASSETS_URL,
  ORDERS_DELIVERED_URL,
  ORDERS_IN_TRANSIT_URL,
  ORDERS_NEW_URL,
  ORDERS_READY_TO_SHIP_URL,
  PRODUCT_LIST_ACTIVE_URL,
  PRODUCT_LIST_DRAFT_URL,
  PRODUCT_LIST_INACTIVE_URL,
  PRODUCT_LIST_OVERVIEW_URL,
  REPORTS_RETURNED_ORDERS_URL,
  REPORTS_SHIPPED_REVENUE_URL,
  STOCK_LEVELS_ALL_URL,
  STOCK_LEVELS_ONLINE_URL,
  STOCK_LEVELS_STORE_URL,
  BRANDS_CONTACT_FORM_URL,
  PAYMENT_CENTRE_URL
} from "constants/urls"

export type MenuItemGuards =
  | `stockLevels`
  | `marketingAssets`
  | `shippedRevenue`
  | `communityHub`
  | `communityHubNewUrl`
  | `orders`
  | `returnedOrders`
  | `homePage`
  | `storeSales`
  | `brandsPayout`

export interface MenuItem {
  id: string
  label: string
  url?: string
  items?: MenuItem[]
  tier?: number
  guards?: MenuItemGuards[]
  invertGuards?: boolean
  isNew?: boolean
  external?: boolean
}

const origin =
  typeof window !== `undefined`
    ? window.location.origin
    : `https://www.wolfandbadger.com`

export const primaryMenuItems: MenuItem[] = [
  {
    id: `home`,
    label: `Home`,
    url: `/`,
    guards: [`homePage`]
  },
  {
    id: `products`,
    label: `Products`,
    items: [
      {
        id: `products/overview`,
        label: `Overview`,
        url: PRODUCT_LIST_OVERVIEW_URL
      },
      {
        id: `products/active`,
        label: `Active`,
        url: PRODUCT_LIST_ACTIVE_URL
      },
      {
        id: `products/draft`,
        label: `Draft`,
        url: PRODUCT_LIST_DRAFT_URL
      },
      {
        id: `products/inactive`,
        label: `Inactive`,
        url: PRODUCT_LIST_INACTIVE_URL
      }
    ]
  },
  {
    id: `stock`,
    label: `Stock levels`,
    guards: [`stockLevels`],
    items: [
      {
        id: `stock/all`,
        label: `All`,
        url: STOCK_LEVELS_ALL_URL,
        guards: [`stockLevels`]
      },
      {
        id: `stock/online`,
        label: `Online`,
        url: STOCK_LEVELS_ONLINE_URL,
        guards: [`stockLevels`]
      },
      {
        id: `stock/in-store`,
        label: `In-store`,
        url: STOCK_LEVELS_STORE_URL,
        guards: [`stockLevels`]
      }
    ]
  },
  {
    id: `orders`,
    label: `Orders`,
    items: [
      {
        id: `orders/legacy`,
        label: `Orders`,
        url: `${origin}/dashboard/orders/`,
        guards: [`orders`],
        invertGuards: true
      },
      {
        id: `orders/new`,
        label: `New`,
        url: ORDERS_NEW_URL,
        guards: [`orders`]
      },
      {
        id: `orders/ready-to-ship`,
        label: `Ready to ship`,
        url: ORDERS_READY_TO_SHIP_URL,
        guards: [`orders`]
      },
      {
        id: `orders/in-transit`,
        label: `In transit`,
        url: ORDERS_IN_TRANSIT_URL,
        guards: [`orders`]
      },
      {
        id: `orders/delivered`,
        label: `Delivered`,
        url: ORDERS_DELIVERED_URL,
        guards: [`orders`]
      },
      {
        id: `orders/returns/legacy`,
        label: `Returns`,
        url: `${origin}/dashboard/orders/incoming-returns/`
      }
    ]
  },
  {
    id: `reporting`,
    label: `Data & Reports`,
    items: [
      {
        id: `reporting/payment-reports`,
        label: `Payment Invoices`,
        url: `${origin}/dashboard/reports/`
      },
      {
        id: `reporting/order-reporting`,
        label: `Order reporting`,
        items: [
          {
            id: `reporting/shipped-revenue`,
            label: `Sales Report`,
            url: REPORTS_SHIPPED_REVENUE_URL,
            guards: [`shippedRevenue`]
          },
          {
            id: `reporting/returned-orders`,
            label: `Returned orders`,
            url: REPORTS_RETURNED_ORDERS_URL,
            guards: [`returnedOrders`]
          },
          {
            id: `analytics/product/declined-orders`,
            label: `Declined orders`,
            url: ANALYTICS_DECLINED_ORDERS_URL
          },
          {
            id: `analytics/product/orders`,
            label: `Orders`,
            url: ANALYTICS_ORDERS_URL
          },
          {
            id: `analytics/product/store-sales`,
            label: `Store Sales`,
            url: ANALYTICS_STORE_SALES_URL,
            guards: [`storeSales`]
          }
        ]
      },
      {
        id: `analytics`,
        label: `Analytics data`,
        items: [
          {
            id: `analytics/customer-insights/conversion-rate`,
            label: `Conversion rate`,
            url: ANALYTICS_CONVERSION_RATE_URL
          },
          {
            id: `analytics/product/views`,
            label: `Product views`,
            url: ANALYTICS_PRODUCT_VIEWS_URL
          },
          {
            id: `analytics/customer-insights/wishlist`,
            label: `Wishlist`,
            url: ANALYTICS_WISHLIST_URL
          }
          // {
          //   id: `analytics/product/click-to-ship`,
          //   label: `Click to ship`,
          //   url: ANALYTICS_CLICK_TO_SHIP_URL
          // }
        ]
      }
    ]
  },
  {
    id: `promotions`,
    label: `Promotions`,
    url: `${origin}/dashboard/promotions/`
  },
  {
    id: `delivery-notes`,
    label: `Delivery Notes`,
    url: `${origin}/dashboard/stock/delivery-note/`
  },
  {
    id: `integrations`,
    label: `Integrations`,
    items: [
      {
        id: `integrations/shopify`,
        label: `Shopify`,
        url: `${origin}/dashboard/integrations/shopify/`
      }
    ]
  },
  {
    id: `marketing`,
    label: `Marketing`,
    guards: [`marketingAssets`],
    items: [
      {
        id: `marketing/assets`,
        label: `Assets`,
        url: MARKETING_ASSETS_URL
      }
    ]
  },
  {
    id: `community-hub`,
    label: `Community hub`,
    url: `https://wolfandbadger-5753238.hs-sites.com/wb-community-hub`,
    guards: [`communityHub`],
    external: true
  },
  {
    id: `community-hub-new-url`,
    label: `Community hub`,
    url: `https://pages.wolfandbadger.com/wb-community-hub`,
    guards: [`communityHubNewUrl`],
    external: true
  }
]

export const secondaryMenuItems: MenuItem[] = [
  {
    id: `profile`,
    label: `Profile`,
    url: `${origin}/dashboard/designer/profile/`
  },
  {
    id: `account`,
    label: `Account settings`,
    url: `${origin}/dashboard/designer/account/`
  },
  {
    id: `payment-centre`,
    label: `Payment Centre`,
    url: PAYMENT_CENTRE_URL,
    guards: [`brandsPayout`]
  },
  {
    id: `help-centre`,
    label: `Help centre`,
    url: `https://kb.wolfandbadger.com/`,
    external: true
  },
  {
    id: `contact-us`,
    label: `Contact us`,
    url: BRANDS_CONTACT_FORM_URL,
    external: true
  },
  {
    id: `logout`,
    label: `Logout`,
    url: `${origin}/accounts/sign-out/`
  }
]
